import { makeStyles } from '@material-ui/core';
import { Grid, Typography } from '@material-ui/core/';
import { withStyles } from '@material-ui/core/styles';
import React, { useState, useMemo } from 'react';
import {
  Button,
  Edit,
  Filter,
  FormTab,
  TextInput,
  TabbedForm,
  TopToolbar,
  Loading,
  useGetList,
  List
} from 'react-admin';
import { FormSpy } from 'react-final-form';
import get from 'lodash/get';
import ViewTab from '../../core/components/forked/layout/ViewTab';
import BulkActionDeleteButton from '../../core/components/BulkActionDeleteButton';
import EditToolbar from '../../core/components/edit/EditToolbar';
import LeaveConfirm from '../../core/components/LeaveConfirmWrapper';
import FormTitleBar from '../../core/components/FormTitleBar';
import Empty from '../../core/components/list/Empty';
import { ListPagination } from '../../core/components/list/ListPagination';
import NavigationPrompt from '../../core/components/NavigationPrompt';
import {
  getInitials,
  predictiveSearchFormatter,
  predictiveSearchParser,
  removePathEnd
} from '../../core/utils';
import GroupCategoryCreateUpdate from '../groupcategories/GroupCategoryCreateUpdate';
import CategoryCreateDialog from './CategoryCreateDialog';
import GroupCategoryDatagrid from './GroupCategoryDatagrid';
import GroupSummary from './GroupSummary';
import GroupSubcategoryCreate from '../groupcategories/GroupSubcategoryCreate';

const useStyles = makeStyles({
  headerContainer: {
    paddingTop: '8px'
  },
  textInput: {
    minWidth: '225px'
  }
});

const GroupCategoryActionStyles = {
  addButton: {
    border: '1px solid #E0E0E0',
    background: '#FFFFFF',
    marginLeft: '10px'
  }
};

const GroupTitle = ({ record }) => {
  return (
    <FormTitleBar
      name={record.name}
      status={record.status}
      statusLabel="Status:"
      showAvatar={true}
      avatarContent={getInitials(record.name)}
    />
  );
};

const GroupRelatedCategoriesLabel = ({ label }) => {
  const classes = useStyles();

  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography variant="h5" className={classes.headerContainer}>
          {label}
        </Typography>
      </Grid>
    </Grid>
  );
};

const CategoriesFilters = props => {
  const classes = useStyles();

  return (
    <Filter variant="standard" {...props}>
      <TextInput
        source="categoryOrSubcategoryName"
        alwaysOn
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        classes={{ root: classes.textInput }}
      />
    </Filter>
  );
};

const GroupCategoryAction = withStyles(GroupCategoryActionStyles)(
  ({
    classes,
    hasWriteGroupCategoryPermission,
    hasCreateCategoryPermission,
    ...props
  }) => (
    <TopToolbar>
      {hasWriteGroupCategoryPermission && hasCreateCategoryPermission && (
        <Button
          color="primary"
          onClick={props.onCategoryCreateButtonClick}
          label="Add New Category"
          className={classes.addButton}
          disableFocusRipple
        />
      )}
      {hasWriteGroupCategoryPermission && (
        <Button
          color="primary"
          onClick={props.onGroupCategoryCreateButtonClick}
          label="Add Existing Category"
          className={classes.addButton}
          disableFocusRipple
        />
      )}
      {hasWriteGroupCategoryPermission && (
        <Button
          color="primary"
          onClick={props.onGroupSubcategoryCreateButtonClick}
          label="Add Existing Subcategory"
          className={classes.addButton}
        />
      )}
    </TopToolbar>
  )
);

const GroupEdit = props => {
  const [whenBlockNavigation, setWhenBlockNavigation] = useState(false);
  const [showCategoryCreateDialog, setShowCategoryCreateDialog] = useState(
    false
  );
  const [
    showGroupCategoryCreateDialog,
    setShowGroupCategoryCreateDialog
  ] = useState(false);
  const [
    showGroupCategoryEditDialog,
    setShowGroupCategoryEditDialog
  ] = useState(false);
  const [
    showGroupSubcategoryCreateDialog,
    setShowGroupSubcategoryCreateDialog
  ] = useState(false);
  const [categoryOrSubcategoryIds, setCategoryOrSubcategoryIds] = useState(
    undefined
  );

  const {
    permissions = [],
    hasCreatePermission,
    hasEditPermission,
    hasDeletePermission,
    userProfile,
    ...rest
  } = props;

  const displayToolbar = props.match.isExact;
  const groupId = props.id;

  const {
    data: tenantConfigurations = {},
    loading: loadingTenantConfigurations
  } = useGetList('tenantconfigurations', null, null, {
    nameOrCategory: 'display_admin_categories'
  });

  const isDisplayAdminCategoriesOn = useMemo(() => {
    return get(Object.values(tenantConfigurations), '[0].value', '0') === '1';
  }, [tenantConfigurations]);

  const groupCategoriesFilter = useMemo(() => {
    return isDisplayAdminCategoriesOn
      ? { groupId }
      : { groupId, subType: 'REPORTING' };
  }, [isDisplayAdminCategoriesOn, groupId]);

  if (loadingTenantConfigurations) return <Loading />;

  const onBeforeDelete = () => {
    // Unblock navigation before deleting
    setWhenBlockNavigation(false);
  };

  const handleFormIsDirty = dirty => {
    // Block navigation if the form is dirty
    setWhenBlockNavigation(dirty);
  };

  /**
   * Open or close dialog to create new category and then add it on current group
   */
  const handleCategoryCreateDialogToggle = () => {
    setShowCategoryCreateDialog(!showCategoryCreateDialog);
  };

  /**
   * Open or close dialog to add an existing category on the current group in edit mode
   */
  const handleGroupCategoryEditDialogToggle = () => {
    setShowGroupCategoryEditDialog(!showGroupCategoryEditDialog);
  };

  /**
   * Open or close dialog to add an existing category on the current group in create mode
   */
  const handleGroupCategoryCreateDialogToggle = () => {
    setShowGroupCategoryCreateDialog(!showGroupCategoryCreateDialog);
  };

  /**
   * To handle the click on button "add an existing category"
   */
  const handleGroupCategoryCreateButtonClick = () => {
    setShowGroupCategoryCreateDialog(!showGroupCategoryCreateDialog);
    setCategoryOrSubcategoryIds(undefined);
  };

  /**
   * Open or close dialog to add an existing subcategory on the current group in create mode
   */
  const handleGroupSubcategoryCreateDialogToggle = () => {
    setShowGroupSubcategoryCreateDialog(!showGroupSubcategoryCreateDialog);
  };

  /**
   * To handle the click on button "add an existing subcategory"
   */
  const handleGroupSubcategoryCreateButtonClick = () => {
    setShowGroupSubcategoryCreateDialog(!showGroupSubcategoryCreateDialog);
    setCategoryOrSubcategoryIds(undefined);
  };

  const hasCreateCategoryPermission = permissions.includes(
    'AUTH_CREATE_CATEGORY'
  );
  const hasWriteGroupCategoryPermission = permissions.includes(
    'AUTH_WRITE_GROUP_CATEGORY'
  );

  return (
    <React.Fragment>
      <NavigationPrompt
        when={whenBlockNavigation}
        adjustMatchingPath={removePathEnd}
        hasEditPermission={hasEditPermission}
      >
        {(isOpen, onConfirm, onCancel) => (
          <LeaveConfirm
            isOpen={isOpen}
            onConfirm={onConfirm}
            onClose={onCancel}
          />
        )}
      </NavigationPrompt>
      <Edit {...rest} undoable={false} title={<GroupTitle />}>
        <TabbedForm
          toolbar={
            displayToolbar ? (
              <EditToolbar
                {...rest}
                hasEditPermission={hasEditPermission}
                hasDeletePermission={hasDeletePermission}
                onBeforeDelete={onBeforeDelete}
              />
            ) : (
              <></>
            )
          }
          variant="standard"
          margin="normal"
        >
          <FormTab label="Group Details">
            <GroupSummary creation={false} />
            <FormSpy
              subscription={{ dirty: true }}
              onChange={props => {
                setTimeout(() => handleFormIsDirty(props.dirty), 1);
              }}
            />
          </FormTab>
          <ViewTab label="Related Categories and Subcategories">
            <GroupRelatedCategoriesLabel label="Related Categories and Subcategories" />
            <List
              style={{ margin: '0px', width: '100%' }}
              filters={<CategoriesFilters />}
              pagination={<ListPagination />}
              basePath="/groupcategories"
              resource="groupcategories"
              filter={groupCategoriesFilter}
              sort={{ field: 'categoryOrSubcategoryId', order: 'ASC' }}
              title=" "
              actions={
                <GroupCategoryAction
                  groupId={groupId}
                  onCategoryCreateButtonClick={handleCategoryCreateDialogToggle}
                  onGroupCategoryCreateButtonClick={
                    handleGroupCategoryCreateButtonClick
                  }
                  onGroupSubcategoryCreateButtonClick={
                    handleGroupSubcategoryCreateButtonClick
                  }
                  hasWriteGroupCategoryPermission={
                    hasWriteGroupCategoryPermission
                  }
                  hasCreateCategoryPermission={hasCreateCategoryPermission}
                />
              }
              bulkActionButtons={
                hasWriteGroupCategoryPermission && <BulkActionDeleteButton />
              }
              empty={
                <Empty
                  emptyText="There are no related Categories to be displayed"
                  buttons={[
                    {
                      label: 'Add New Category',
                      onClick: handleCategoryCreateDialogToggle,
                      hide: !(
                        hasWriteGroupCategoryPermission &&
                        hasCreateCategoryPermission
                      )
                    },
                    {
                      label: 'Add Existing Category',
                      onClick: handleGroupCategoryCreateButtonClick,
                      hide: !hasWriteGroupCategoryPermission
                    }
                  ]}
                />
              }
              syncWithLocation
            >
              <GroupCategoryDatagrid permissions={permissions} {...rest} />
            </List>
            <GroupCategoryCreateUpdate
              operation="Update"
              title=" "
              showDialog={showGroupCategoryEditDialog}
              handleDialogToggle={handleGroupCategoryEditDialogToggle}
              basePath="/groupcategories"
              resource="groupcategories"
              groupId={groupId}
              categoryOrSubcategoryId={categoryOrSubcategoryIds}
            />
            <CategoryCreateDialog
              title=" "
              showDialog={showCategoryCreateDialog}
              handleDialogToggle={handleCategoryCreateDialogToggle}
              basePath="/groupcategories"
              resource="groupcategories"
              groupId={groupId}
            />
            <GroupCategoryCreateUpdate
              operation="Create"
              title=" "
              showDialog={showGroupCategoryCreateDialog}
              handleDialogToggle={handleGroupCategoryCreateDialogToggle}
              basePath="/groupcategories"
              resource="groupcategories"
              groupId={groupId}
              categoryOrSubcategoryIds={categoryOrSubcategoryIds}
              isDisplayAdminCategoriesOn={isDisplayAdminCategoriesOn}
            />
            <GroupSubcategoryCreate
              title=" "
              showDialog={showGroupSubcategoryCreateDialog}
              handleDialogToggle={handleGroupSubcategoryCreateDialogToggle}
              isDisplayAdminCategoriesOn={isDisplayAdminCategoriesOn}
              basePath="/groupcategories"
              resource="groupcategories"
              groupId={groupId}
            />
          </ViewTab>
        </TabbedForm>
      </Edit>
    </React.Fragment>
  );
};

export default GroupEdit;
